import { apiClient, type ApiResponse } from './client'
import type {
  CumulativeAssessmentDetailView,
  CumulativeAssessmentReportView,
  CumulativeGrowthPointView,
  MathHeadRunSummaryView,
} from '@/types/coaching'

const base = (learnerId: number) => `/lc/admin/coaching/learners/${learnerId}/cumulative-assessments`

export interface MathHeadRetestView {
  assessmentId: number
  runId: number
  roundNo: number
  status: 'READY' | 'IN_PROGRESS'
}

export const cumulativeAssessmentAdminApi = {
  getDetail: async (learnerId: number, assessmentId: number) => {
    const response = await apiClient.get<ApiResponse<CumulativeAssessmentDetailView>>(`${base(learnerId)}/${assessmentId}`)
    return response.data.data
  },
  getReport: async (learnerId: number, assessmentId: number) => {
    const response = await apiClient.get<ApiResponse<CumulativeAssessmentReportView>>(`${base(learnerId)}/${assessmentId}/report`)
    return response.data.data
  },
  /**
   * Growth graph: the learner's reports across every enrollment, oldest first (D11), ending at
   * [throughSequence] so a report never shows rounds taken after it.
   */
  getGrowth: async (learnerId: number, throughSequence: number, limit = 10) => {
    const response = await apiClient.get<ApiResponse<CumulativeGrowthPointView[]>>(
      `/lc/admin/coaching/learners/${learnerId}/cumulative-reports/growth`,
      { params: { limit, throughSequence } },
    )
    return response.data.data
  },
  /** Cumulative Overall Comment; a blank comment clears it. 409 CUMULATIVE_REPORT_VERSION_CONFLICT on a stale version. */
  updateOverallComment: async (learnerId: number, assessmentId: number, comment: string, expectedVersion: number) => {
    const response = await apiClient.put<ApiResponse<{ overallComment?: string | null; reportVersion: number }>>(
      `${base(learnerId)}/${assessmentId}/report/overall-comment`,
      { comment, expectedVersion },
    )
    return response.data.data
  },
  createChapterAttempt: async (learnerId: number, assessmentId: number) => {
    const response = await apiClient.post<ApiResponse<{ attempt: { testId: number } }>>(
      `${base(learnerId)}/${assessmentId}/chapter-comprehensive/attempts`,
    )
    return response.data.data
  },
  /** Creates a READY Math Head run when the latest run is completed/cancelled (BIGGO is not called). */
  createMathHeadRetest: async (learnerId: number, assessmentId: number) => {
    const response = await apiClient.post<ApiResponse<MathHeadRetestView>>(`${base(learnerId)}/${assessmentId}/math-head/retests`)
    return response.data.data
  },
  /** Math Head Run Cancellation: READY/IN_PROGRESS only; BIGGO's answer is authoritative (a past-deadline run comes back COMPLETED). */
  cancelMathHeadRun: async (learnerId: number, assessmentId: number, runId: number, reason: string) => {
    const response = await apiClient.post<ApiResponse<MathHeadRunSummaryView>>(
      `${base(learnerId)}/${assessmentId}/math-head/runs/${runId}/cancel`,
      { reason },
    )
    return response.data.data
  },
  /**
   * 누적 테스트 마감. 두 세그먼트가 모두 종결된 뒤에만 허용되고(아니면 409
   * CUMULATIVE_SEGMENTS_NOT_TERMINAL), 마감과 함께 리포트가 발행되고 학습자의 다음 레슨이 열린다.
   */
  resolve: async (learnerId: number, assessmentId: number) => {
    const response = await apiClient.post<ApiResponse<CumulativeAssessmentDetailView>>(
      `${base(learnerId)}/${assessmentId}/resolve`,
    )
    return response.data.data
  },
  exempt: async (
    learnerId: number,
    assessmentId: number,
    segmentType: 'CHAPTER_COMPREHENSIVE' | 'MATH_HEAD',
    reason: string,
  ) => {
    const response = await apiClient.post<ApiResponse<unknown>>(
      `${base(learnerId)}/${assessmentId}/segments/${segmentType}/exemption`,
      { reason },
    )
    return response.data.data
  },
}
